import { useEffect, useRef, useState } from 'react'
import Daily, {
  type DailyCall,
  type DailyEventObjectTrack,
  type DailyEventObjectTranscriptionMessage,
  type DailyParticipant,
} from '@daily-co/daily-js'

export type DailyTranscriptFinal = {
  text: string
  participantId: string
  userId: string
  local: boolean
  utteranceId: string
}

export type DailyCallState = 'idle' | 'joining' | 'joined' | 'error' | 'left'

function participantsList(call: DailyCall): DailyParticipant[] {
  const map = call.participants()
  return Object.values(map)
}

export function useDailyCall({
  roomUrl,
  token,
  enabled,
  onFinalTranscript,
}: {
  roomUrl?: string
  token?: string
  enabled: boolean
  onFinalTranscript?: (evt: DailyTranscriptFinal) => void
}) {
  const callRef = useRef<DailyCall | null>(null)
  const transcriptRef = useRef(onFinalTranscript)
  transcriptRef.current = onFinalTranscript

  const [state, setState] = useState<DailyCallState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [participants, setParticipants] = useState<DailyParticipant[]>([])

  useEffect(() => {
    if (!enabled || !roomUrl || !token) return

    let cancelled = false
    const call = Daily.createCallObject({ subscribeToTracksAutomatically: true })
    callRef.current = call
    setState('joining')
    setError(null)

    const syncParticipants = () => {
      if (cancelled) return
      setParticipants(participantsList(call))
    }

    const onJoined = () => {
      if (cancelled) return
      setState('joined')
      syncParticipants()
    }
    const onLeft = () => {
      if (cancelled) return
      setState('left')
    }
    const onError = (evt: { errorMsg?: string }) => {
      if (cancelled) return
      setState('error')
      setError(evt.errorMsg || 'Video room error')
    }
    const onParticipant = () => {
      syncParticipants()
    }
    const onTrack = (_evt: DailyEventObjectTrack) => {
      syncParticipants()
    }
    const onTranscript = (evt: DailyEventObjectTranscriptionMessage) => {
      const text = String(evt.text || '').trim()
      if (!text) return
      const raw = evt.rawResponse
      if (raw && raw.is_final === false) {
        return
      }
      const participantId = evt.participantId || ''
      const participant = call.participants()[participantId]
      const userId = String(participant?.user_id || '')
      const utteranceId = String(
        evt.instanceId || evt.timestamp?.toISOString?.() || `${participantId}:${text.slice(0, 24)}`,
      )
      transcriptRef.current?.({
        text,
        participantId,
        userId,
        local: Boolean(participant?.local),
        utteranceId,
      })
    }

    call.on('joined-meeting', onJoined)
    call.on('left-meeting', onLeft)
    call.on('error', onError)
    call.on('participant-joined', onParticipant)
    call.on('participant-updated', onParticipant)
    call.on('participant-left', onParticipant)
    call.on('track-started', onTrack)
    call.on('track-stopped', onTrack)
    call.on('transcription-message', onTranscript)

    void call
      .join({ url: roomUrl, token })
      .then(() => {
        if (!cancelled) syncParticipants()
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setState('error')
        setError(err instanceof Error ? err.message : 'Could not join the video room')
      })

    return () => {
      cancelled = true
      call.off('joined-meeting', onJoined)
      call.off('left-meeting', onLeft)
      call.off('error', onError)
      call.off('participant-joined', onParticipant)
      call.off('participant-updated', onParticipant)
      call.off('participant-left', onParticipant)
      call.off('track-started', onTrack)
      call.off('track-stopped', onTrack)
      call.off('transcription-message', onTranscript)
      void call.leave().finally(() => {
        call.destroy()
      })
      if (callRef.current === call) callRef.current = null
    }
  }, [enabled, roomUrl, token])

  return { state, error, participants, call: callRef.current }
}

export function attachVideoTrack(
  el: HTMLVideoElement | null,
  participant: DailyParticipant | undefined,
) {
  if (!el || !participant) {
    if (el) el.srcObject = null
    return
  }
  const track = participant.tracks.video?.persistentTrack
  if (!track) {
    el.srcObject = null
    return
  }
  const current = el.srcObject
  if (current instanceof MediaStream && current.getVideoTracks()[0] === track) {
    return
  }
  el.srcObject = new MediaStream([track])
}
